/**
 * One group run under a pairing on the Cohorts tab (GROUP-ORIENTATION-PLAN
 * Phase 5): status, leader, "x of y joined", per-member connected/check
 * state, and the status-appropriate controls. Presentation only.
 */
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { useState } from 'react';
import type { GroupRunRowData } from '@/hooks/useGroupRuns';
import { isRunLeader, joinedTally, lobbyWaitText, runControls } from '@/lib/groupRunEntity';

const STATUS_STYLE: Record<string, { bg: string; fg: string }> = {
  Lobby: { bg: 'var(--panel-3)', fg: 'var(--muted)' },
  Running: { bg: 'var(--good-soft)', fg: 'var(--good)' },
  Paused: { bg: 'var(--warn-soft)', fg: 'var(--warn)' },
  Completed: { bg: 'var(--good-soft)', fg: 'var(--good)' },
  Cancelled: { bg: 'var(--warn-soft)', fg: 'var(--warn)' },
};

export function GroupRunRow({
  run,
  busy,
  currentUserEmail,
  onStart,
  onPause,
  onResume,
  onHandOver,
  onCancel,
  onJoin,
  onUpdateSettings,
}: {
  run: GroupRunRowData;
  busy: boolean;
  currentUserEmail: string;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  /** Prompted with an email — take-over passes the caller's own. */
  onHandOver: (newLeaderEmail: string) => void;
  onCancel: () => void;
  onJoin: () => void;
  /** Lobby only: update_group_run with the changed settings (Phase 10). */
  onUpdateSettings: (settings: { allowLateJoin: boolean; autoStallSeconds: number }) => void;
}) {
  const [showMembers, setShowMembers] = useState(false);
  const dialogs = useDialogs();
  const tally = joinedTally(run.members);
  const controls = runControls(run.statusTag);
  const style = STATUS_STYLE[run.statusTag] ?? STATUS_STYLE.Lobby;
  const leaderName = run.leaderEmail.split('@')[0];
  const iLead = isRunLeader(run.leaderEmail, currentUserEmail);

  return (
    <div style={{ borderTop: '1px solid var(--border-soft, var(--border))', padding: '8px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 12.5 }}>{run.name}</b>
        <span
          style={{
            padding: '2px 8px',
            borderRadius: 10,
            background: style.bg,
            color: style.fg,
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          {run.statusTag}
        </span>
        <button
          type="button"
          className="btn sm ghost"
          style={{ padding: '0 6px', fontSize: 11 }}
          onClick={() => setShowMembers((v) => !v)}
        >
          {tally.joined} of {tally.total} joined
        </button>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>
          {run.modeTag} · leader {leaderName}
          {run.statusTag === 'Lobby' ? ` · ${lobbyWaitText(run.modeTag, leaderName)}` : ''}
          {run.statusTag === 'Paused' && run.pausedByEmail
            ? ` · paused by ${run.pausedByEmail.split('@')[0]}`
            : ''}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {controls.start ? (
            <button type="button" className="btn sm primary" disabled={busy} onClick={onStart}>
              Start
            </button>
          ) : null}
          {controls.pause ? (
            <button
              type="button"
              className="btn sm ghost"
              disabled={busy}
              title="Pause the group. Everyone stays in the room; Resume picks up where it stopped."
              onClick={onPause}
            >
              Pause
            </button>
          ) : null}
          {controls.resume ? (
            <button type="button" className="btn sm primary" disabled={busy} onClick={onResume}>
              Resume
            </button>
          ) : null}
          {controls.join ? (
            <button
              type="button"
              className="btn sm ghost"
              disabled={busy}
              title={
                iLead
                  ? 'Open the MineZero client in the session of this run, as its leader.'
                  : 'Watch this run from inside the MineZero client as an observer.'
              }
              onClick={onJoin}
            >
              {iLead ? (run.statusTag === 'Lobby' ? 'Enter lobby' : 'Enter as leader') : 'Join'}
            </button>
          ) : null}
          {controls.handOver ? (
            <button
              type="button"
              className="btn sm ghost"
              disabled={busy}
              title="Take over yourself, or hand leadership to another site user."
              onClick={async () => {
                const next = await dialogs.prompt({
                  title: `Hand over "${run.name}"`,
                  label: 'New leader email',
                  placeholder: currentUserEmail,
                  hint: 'Leave empty to take over yourself.',
                  allowEmpty: true,
                  confirmLabel: 'Hand over',
                });
                if (next === null) return;
                onHandOver(next || currentUserEmail);
              }}
            >
              Hand over
            </button>
          ) : null}
          {controls.cancel ? (
            <button type="button" className="btn sm danger" disabled={busy} onClick={onCancel}>
              Cancel
            </button>
          ) : null}
        </span>
      </div>
      {run.statusTag === 'Lobby' ? (
        <div style={{ marginTop: 4, display: 'flex', gap: 14, alignItems: 'center', paddingLeft: 8, flexWrap: 'wrap' }}>
          <label
            style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11.5, color: 'var(--muted)' }}
            title="Off: a member who never started cannot enter once the run is Running. A dropped member can always rejoin. Editable while the run is in the lobby."
          >
            <input
              type="checkbox"
              checked={run.allowLateJoin}
              disabled={busy}
              onChange={(e) =>
                onUpdateSettings({
                  allowLateJoin: e.target.checked,
                  autoStallSeconds: run.autoStallSeconds,
                })
              }
              style={{ width: 13, height: 13 }}
            />
            Allow late joiners
          </label>
          {run.modeTag === 'Auto' ? (
            <label
              style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11.5, color: 'var(--muted)' }}
              title="Once the first member finishes a stop, the run moves on after this many seconds (open checks expire). 0 = wait forever. Editable while the run is in the lobby."
            >
              Stall seconds
              <input
                type="number"
                className="inp"
                min={0}
                max={3600}
                defaultValue={run.autoStallSeconds}
                disabled={busy}
                onBlur={(e) => {
                  const v = Number.parseInt(e.target.value, 10);
                  const next = Number.isFinite(v) && v >= 0 ? v : 0;
                  if (next !== run.autoStallSeconds)
                    onUpdateSettings({ allowLateJoin: run.allowLateJoin, autoStallSeconds: next });
                }}
                aria-label="Stall seconds (0 = never), saved when you leave the field"
                style={{ width: 70, fontSize: 11.5, padding: '2px 6px' }}
              />
            </label>
          ) : null}
        </div>
      ) : null}
      {showMembers ? (
        <div style={{ marginTop: 4, display: 'grid', gap: 2, paddingLeft: 8 }}>
          {run.members.map((m) => (
            <div key={m.email} style={{ display: 'flex', gap: 8, fontSize: 11.5, alignItems: 'baseline' }}>
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: m.connected ? 'var(--good)' : 'var(--panel-3)',
                  border: '1px solid var(--border)',
                  flexShrink: 0,
                  alignSelf: 'center',
                }}
                title={m.connected ? 'Connected' : 'Not joined yet'}
              />
              <span style={{ minWidth: 200, color: m.connected ? 'var(--text)' : 'var(--muted)' }}>
                {m.displayName || m.email}
              </span>
              {m.pendingChecks > 0 ? (
                <span style={{ color: 'var(--warn)' }}>
                  {m.pendingChecks} check{m.pendingChecks === 1 ? '' : 's'} open
                </span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
